import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	ElementRef,
	inject,
	OnInit,
	ViewChild,
	ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';

import { Language, TranslatePipe, TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import { MarkdownModule } from 'ngx-markdown';
import { map, Observable, switchMap } from 'rxjs';

import { AlertComponent } from '@valk-nx/components/ui-alert/src/lib/alert.component';
import { CarouselComponent } from '@valk-nx/components/ui-carousel/src/lib/carousel';
import { CarouselItem } from '@valk-nx/components/ui-carousel/src/lib/carousel.interface';
import { ImageInterface } from '@valk-nx/components/ui-image/src/lib/image.interface';
import { ListGridComponent } from '@valk-nx/components/ui-list-grid/src/lib/list-grid';
import { MapComponent } from '@valk-nx/components/ui-map/src/lib/map';
import { MiniCarouselComponent } from '@valk-nx/components/ui-mini-carousel/src/lib/mini-carousel';
import { RatingComponent } from '@valk-nx/components/ui-rating/src/lib/rating';
import { SheetModule } from '@valk-nx/components/ui-sheet/src/lib/sheet.module';
import { TabMenuComponent } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu';
import { TabMenuItemDirective } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu.directive';
import {
	AvailabilityWidgetComponent,
	WidgetDataActionInterface,
} from '@valk-nx/compositions/ui-availability-widget/src/lib/availability-widget.component';
import { PersuadeCardComponent } from '@valk-nx/compositions/ui-persuade-card/src/lib/persuade-card.component';
import {
	fallbackLanguage,
	LANGUAGE,
	negativeHeader$,
} from '@valk-nx/core/lib/core';
import { PhotoInterface } from '@valk-nx/core/lib/interfaces/image.interface';
import { AccommodationTypeModule } from '@valk-nx/core/lib/pipes/accommodation-type/accommodation-type.module';
import { BookingRedirectHelper } from '@valk-nx/helpers/lib/booking-redirect/booking-redirect.helper';
import { ViewPortService } from '@valk-nx/services/viewport/src/lib/viewport.service';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { BOOKINGTOOL } from '@valk-nx/storyblok-services/src/lib/globals';
import { HotelDealData } from '@valk-nx/storyblok-services/src/lib/services/content.interface';
import { ContentService } from '@valk-nx/storyblok-services/src/lib/services/content.service';
import { MetadataService } from '@valk-nx/storyblok-services/src/lib/services/metadata.service';
import { StoryblokService } from '@valk-nx/storyblok-services/src/lib/services/storyblok.service';
import { TagManagerFacade } from '@valk-nx/storyblok-store/src/lib/store/tag-manager/tag-manager.facade';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	selector: 'sb-hotel-by-deal-detail',
	templateUrl: './hotel-by-deal-detail.component.html',
	imports: [
		AccommodationTypeModule,
		AlertComponent,
		AvailabilityWidgetComponent,
		CarouselComponent,
		CommonModule,
		ListGridComponent,
		MapComponent,
		MarkdownModule,
		MiniCarouselComponent,
		PersuadeCardComponent,
		RatingComponent,
		RouterModule,
		SheetModule,
		TranslatePipe,
		TabMenuComponent,
		TabMenuItemDirective,
	],
})
export class HotelByDealDetailComponent
	extends StoryblokRootDirective
	implements OnInit
{
	private readonly sbService = inject(StoryblokService);
	private readonly contentService = inject(ContentService);
	private readonly metadataService = inject(MetadataService);
	private readonly viewportService = inject(ViewPortService);
	private readonly tagManager = inject(TagManagerFacade);
	private readonly translate = inject(TranslateService);
	private readonly bookingtool = inject(BOOKINGTOOL);
	private readonly destroyRef = inject(DestroyRef);
	private readonly rootLanguage = inject<Language>(LANGUAGE);

	@ViewChild('contentRef', { read: ElementRef }) contentRef!: ElementRef;

	isSmallTablet$ = this.viewportService.isSmallTablet$;
	hotelDealData$!: Observable<HotelDealData>;

	language: string = fallbackLanguage;
	dealSlug = '';
	hotelSlug = '';
	carouselImages: CarouselItem[] = [];
	hotelImages: ImageInterface[] = [];
	dealImages: PhotoInterface[] = [];
	dealStatus: 'available' | 'expired' | 'future' | 'unavailable' =
		'available';

	maxDate!: Date;
	minDate!: Date;

	constructor() {
		super();
		this.language = this.rootLanguage;
	}

	ngOnInit(): void {
		this.hotelDealData$ = this.loadHotelData();
	}

	widgetCTA(eventData: {
		event: string;
		eventType: string;
		data: WidgetDataActionInterface;
	}): void {
		this.tagManager.availabilityWidget({
			...eventData,
			data: {
				...eventData.data,
				hotelSlug: this.hotelSlug,
				dealSlug: this.dealSlug,
			},
		});
	}

	private loadHotelData(): Observable<HotelDealData> {
		return this.sbService.translatedSlugs$.pipe(
			takeUntilDestroyed(this.destroyRef),
			map((slugs) => slugs.find((x) => x.selected) ?? slugs[0]),
			switchMap((slug) => {
				const pathSegments = slug.path.split('/');
				this.dealSlug = pathSegments.at(-2);
				this.hotelSlug = pathSegments.at(-1);

				return this.contentService.getHotelByDeal(
					this.dealSlug,
					this.hotelSlug,
					slug.lang.toLowerCase(),
				);
			}),
			map((response) => this.mapHotelDealData(response.data)),
		);
	}

	private mapHotelDealData(data: HotelDealData): HotelDealData {
		const deal = data.deal;

		data.bookingToolRedirectUrl = BookingRedirectHelper.getRedirectUrl(
			this.bookingtool,
			this.hotelSlug,
			'',
			deal.guid,
			this.language,
		);

		this.setDealStatus(data);

		this.metadataService.updateMetaDataBatch(data);

		this.carouselImages = data.images.map((img) => ({
			image: { src: img.src, alt: img.alt },
			heading: deal.name,
			subHeading: this.translate.instant(
				'storyblok.pages.hotel-by-deal.carousel.deal-at-hotel',
				{ hotelName: data.hotel.name },
			),
			link: null,
		}));

		if (this.carouselImages.length > 0) {
			negativeHeader$.next(true);
		}

		const { bookable } = deal;

		this.maxDate =
			bookable?.to && dayjs(bookable.to).isAfter(dayjs(), 'day')
				? dayjs(bookable.to).toDate()
				: dayjs().add(1, 'year').toDate();
		this.minDate =
			bookable?.from && dayjs(bookable.from).isAfter(dayjs(), 'day')
				? dayjs(bookable.from).toDate()
				: dayjs().subtract(1, 'day').toDate();

		this.hotelImages = data.hotel.images.map((img) => ({
			src: img.src,
			altText: img.alt,
			width: 0,
			height: 0,
		}));

		this.dealImages = data.deal.images.map((img) => ({
			src: img.src,
			alt: img.alt,
		}));

		return data;
	}

	private setDealStatus(dealData: HotelDealData): void {
		if (dealData.deal.dealExpired) {
			this.dealStatus = 'expired';
		} else if (dealData.deal.dealAvailableSoon) {
			this.dealStatus = 'future';
		} else if (!dealData.isAvailable) {
			this.dealStatus = 'unavailable';
		} else {
			this.dealStatus = 'available';
		}
	}
}
